import { kitText, type KitMessageKey } from './messages.ts';
import { formatCount, formatDate, formatMoney } from './present.ts';

/**
 * What the platform held when a dispute was raised — recorded by the server at
 * that moment, never supplied by the raiser. The brand investigates weeks
 * later, when the same report may read differently; this is what the
 * affiliate was looking at.
 */
export interface DisputeRaisedContext {
  readonly source: 'CLICK_DISCREPANCY' | 'DISPUTES_FORM' | 'API';
  readonly captured_at: string;
  readonly campaign: { readonly version_id: string; readonly name: string } | null;
  readonly period: {
    readonly id: string; readonly starts_at: string; readonly ends_at: string; readonly state: string;
  } | null;
  /** CLICK_DISCREPANCY — the range the report was read for; the end is exclusive. */
  readonly range?: { readonly from: string; readonly to_exclusive: string };
  readonly product?: string | null;
  readonly reported_by_affiliate?: number | null;
  readonly figures?: Readonly<Record<string, number | null | undefined>>;
  readonly by_sub_id?: readonly {
    readonly sub_id: string | null;
    readonly ours_valid: number; readonly bot: number; readonly duplicate: number;
    readonly geo_blocked: number; readonly os_blocked: number; readonly device_blocked: number;
    readonly targeting_blocked: number;
  }[];
  /** DISPUTES_FORM — the affiliate's commission in the period they named, by type. */
  readonly commission?: readonly {
    readonly type: string; readonly currency: string; readonly lines: number; readonly amount_minor: string;
  }[];
}

export interface DisputeContextTable {
  readonly caption: string;
  readonly columns: readonly string[];
  readonly rows: readonly (readonly string[])[];
}

export interface DisputeContextView {
  readonly title: string;
  readonly facts: readonly { readonly label: string; readonly value: string }[];
  readonly figures: readonly { readonly label: string; readonly value: string }[];
  readonly tables: readonly DisputeContextTable[];
  readonly note: string;
}

const SOURCE: Record<DisputeRaisedContext['source'], KitMessageKey> = {
  CLICK_DISCREPANCY: 'ui.ctx.source.CLICK_DISCREPANCY',
  DISPUTES_FORM: 'ui.ctx.source.DISPUTES_FORM',
  API: 'ui.ctx.source.API',
};

/** In the order the discrepancy panel reads them: what we counted, why, then the gap. */
const FIGURES: readonly (readonly [string, KitMessageKey])[] = [
  ['ours_total', 'ui.ctx.fig.ours_total'],
  ['ours_valid', 'ui.ctx.fig.ours_valid'],
  ['bot', 'ui.ctx.fig.bot'],
  ['duplicate', 'ui.ctx.fig.duplicate'],
  ['geo_blocked', 'ui.ctx.fig.geo_blocked'],
  ['os_blocked', 'ui.ctx.fig.os_blocked'],
  ['device_blocked', 'ui.ctx.fig.device_blocked'],
  ['targeting_blocked', 'ui.ctx.fig.targeting_blocked'],
  ['affiliate_tracker_total', 'ui.ctx.fig.affiliate_tracker_total'],
  ['gap_vs_valid', 'ui.ctx.fig.gap_vs_valid'],
  ['explained_rejects', 'ui.ctx.fig.explained_rejects'],
  ['unexplained_gap', 'ui.ctx.fig.unexplained_gap'],
];

const dayBefore = (iso: string) => new Date(Date.parse(iso) - 86_400_000).toISOString();

/**
 * The snapshot as the reader sees it. Formatting lives here rather than in the
 * component because money does: a `.tsx` may not format an amount
 * (check-boundaries.mjs), and the commission table is amounts.
 */
export function disputeContextView(ctx: DisputeRaisedContext): DisputeContextView {
  const facts: { label: string; value: string }[] = [
    { label: kitText('ui.ctx.source'), value: kitText(SOURCE[ctx.source] ?? 'ui.ctx.source.API') },
    { label: kitText('ui.ctx.captured'), value: formatDate(ctx.captured_at) },
  ];
  if (ctx.range) {
    facts.push({
      label: kitText('ui.ctx.range'),
      value: `${formatDate(ctx.range.from)} – ${formatDate(dayBefore(ctx.range.to_exclusive))}`,
    });
  }
  if (ctx.campaign) facts.push({ label: kitText('ui.ctx.campaign'), value: ctx.campaign.name });
  if (ctx.product) facts.push({ label: kitText('ui.ctx.product'), value: ctx.product });
  if (ctx.period) {
    facts.push({
      label: kitText('ui.ctx.period'),
      value: `${formatDate(ctx.period.starts_at)} – ${formatDate(ctx.period.ends_at)}`,
    });
  }

  const figures = FIGURES
    .filter(([key]) => typeof ctx.figures?.[key] === 'number')
    .map(([key, label]) => ({ label: kitText(label), value: formatCount(ctx.figures![key] as number) }));

  const tables: DisputeContextTable[] = [];
  if (ctx.by_sub_id && ctx.by_sub_id.length > 0) {
    tables.push({
      caption: kitText('ui.ctx.subIds'),
      columns: [
        kitText('ui.ctx.col.subId'), kitText('ui.ctx.fig.ours_valid'), kitText('ui.ctx.fig.bot'),
        kitText('ui.ctx.fig.duplicate'), kitText('ui.ctx.fig.geo_blocked'), kitText('ui.ctx.fig.os_blocked'),
        kitText('ui.ctx.fig.device_blocked'), kitText('ui.ctx.fig.targeting_blocked'),
      ],
      rows: ctx.by_sub_id.map((r) => [
        r.sub_id || '—',
        ...[r.ours_valid, r.bot, r.duplicate, r.geo_blocked, r.os_blocked, r.device_blocked, r.targeting_blocked]
          .map((n) => formatCount(n)),
      ]),
    });
  }
  if (ctx.commission && ctx.commission.length > 0) {
    tables.push({
      caption: kitText('ui.ctx.commission'),
      columns: [kitText('ui.ctx.col.type'), kitText('ui.ctx.col.lines'), kitText('ui.ctx.col.amount')],
      rows: ctx.commission.map((c) => [
        c.type,
        formatCount(c.lines),
        formatMoney({ amount_minor: c.amount_minor, currency: c.currency }),
      ]),
    });
  }

  return { title: kitText('ui.ctx.title'), facts, figures, tables, note: kitText('ui.ctx.note') };
}
